import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail, downloadBlobResponse } from "@/lib/api";
import { teksLama, ringkasTeksLama } from "@/lib/teksLama";
import { BIODATA_PENANDA, FILTER_BIODATA_SEMUA, FILTER_BIODATA_IDENTITAS, FILTER_BIODATA_NILAI } from "@/lib/biodataWajib";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProdiSelect from "@/components/ProdiSelect";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, Search, FileSpreadsheet, FileText, Upload, History, Lock, X, PencilRuler, ListChecks, ChevronLeft, ChevronRight, Archive, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { formatWIB } from "@/lib/datetime";
import { seesAllProdi, canDeletePeserta, isSuperAdmin, isAdminKoas, istilahProdi, bolehUbahPeriodeCuti } from "@/lib/roles";
import { STASE_BELUM, STASE_OPSI, opsiStase } from "@/lib/stase";
import ImportPesertaDialog from "@/components/ImportPesertaDialog";
import RotasiKlinikTab from "@/components/RotasiKlinikTab";
import PeriodeCutiSection from "@/components/PeriodeCutiSection";
import KunciStaseRotasi from "@/components/KunciStaseRotasi";
import StickyHeader from "@/components/StickyHeader";
import { teksRentangPeriode } from "@/lib/periodeEdit";
import { BANTUAN_NO_HP, CONTOH_NO_HP, PANJANG_MAKS_NO_HP, bersihkanTempelanNoHp, galatNoHpForm } from "@/lib/nomorHp";

const EMPTY = {
    nama: "", npm: "", prodi_id: "", jenjang: "Sp1", tahap: "1", peminatan: "",
    tempat_lahir: "", tanggal_lahir: "",
    kewarganegaraan: "WNI", jenis_identitas: "NIK", nik: "", negara_penerbit_paspor: "",
    alamat: "", no_hp: "", email: "",
    semester_tahun_masuk: "", semester: null, periode: null, tahun_akademik_mulai: null,
    status_keaktifan: "Aktif", bulan_tahun_lulus: "",
    stase: "Stase RSCM", pembiayaan_pendidikan: "", keterangan_pegawai_rscm: "", catatan_lain: "",
    asal_universitas: "",
};

const STATUS_STYLE = {
    "Aktif": "bg-emerald-100 text-emerald-700 border-emerald-300",
    "Cuti": "bg-amber-100 text-amber-700 border-amber-300",
    "Lulus": "bg-blue-100 text-blue-700 border-blue-300",
    "Tidak Aktif": "bg-slate-100 text-slate-600 border-slate-300",
    "Meninggal": "bg-slate-700 text-slate-100 border-slate-700",
};
// Status di mana Stase tidak wajib
const STATUS_TANPA_STASE = ["Lulus", "Meninggal", "Cuti", "Tidak Aktif"];
// Status di mana Semester & Periode Masuk tidak wajib (keduanya sama-sama dikecualikan,
// keputusan 24 Sep 2026) -- boleh tetap diisi kalau datanya ada.
const STATUS_TANPA_SEMESTER_WAJIB = ["Lulus", "Meninggal"];
const SEMESTER_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);
// Rentang & kalender akademik sama dgn backend (TAHUN_AKADEMIK_MIN/_tahun_akademik_max
// di server.py) -- floor tetap, ceiling dihitung dinamis (tahun akademik berjalan + 1)
// supaya kedua sisi otomatis tetap sinkron. Ganjil = Juli-Desember, Genap = Januari-Juni.
const TAHUN_AKADEMIK_MIN = 2017;
function currentTahunAkademikMulai() {
    const now = new Date();
    const bulan = now.getMonth() + 1; // 1-12
    return bulan > 6 ? now.getFullYear() : now.getFullYear() - 1;
}
const TAHUN_AKADEMIK_MAX = currentTahunAkademikMulai() + 1;
// Diurut dari yg paling baru (Genap tahun_mulai+1 secara kalender terjadi SETELAH
// Ganjil tahun_mulai, jadi Genap ditaruh duluan dlm tiap pasang tahun akademik).
const PERIODE_MASUK_OPTIONS = [];
for (let y = TAHUN_AKADEMIK_MAX; y >= TAHUN_AKADEMIK_MIN; y--) {
    PERIODE_MASUK_OPTIONS.push({ value: `Genap-${y}`, label: `Genap ${y}/${y + 1}`, periode: "Genap", tahun: y });
    PERIODE_MASUK_OPTIONS.push({ value: `Ganjil-${y}`, label: `Ganjil ${y}/${y + 1}`, periode: "Ganjil", tahun: y });
}
// Ringkasan Semester+Periode Masuk utk baris tabel -- tampilkan cuma yg terisi
// (bukan "Sem — · —" kalau dua2nya kosong, cukup 1 pesan jelas).
function formatSemesterPeriode(r) {
    const semTxt = r.semester ? `Sem ${r.semester}` : null;
    const periodeTxt = (r.periode && r.tahun_akademik_mulai != null)
        ? `${r.periode} ${r.tahun_akademik_mulai}/${r.tahun_akademik_mulai + 1}`
        : null;
    if (!semTxt && !periodeTxt) return "Semester belum diisi";
    return [semTxt, periodeTxt].filter(Boolean).join(" · ");
}
// Baris per halaman utk tabel Peserta Didik. Backend membatasi max 200 (lihat
// GET /peserta di server.py) -- diagnosis: fetch semua 2700+ baris sekaligus
// adalah penyebab utama lambatnya halaman ini, jadi dipaginasi di server.
const PAGE_SIZE = 100;

const BULK_FIELDS = {
    status_keaktifan: { label: "Status Keaktifan", options: ["Aktif", "Tidak Aktif", "Cuti", "Lulus", "Meninggal"] },
    tahap: { label: "Tahap", options: ["1", "2", "3", "MDU/MDK"] },
    stase: { label: "Stase", options: STASE_OPSI },
    prodi_id: { label: "Prodi" },
};
const STASE_STYLE = {
    "Stase RSCM": "bg-sky-100 text-sky-700 border-sky-300",
    "Stase Luar": "bg-purple-100 text-purple-700 border-purple-300",
    "MDU-MDK": "bg-teal-100 text-teal-700 border-teal-300",
    "Penelitian": "bg-orange-100 text-orange-700 border-orange-300",
    [STASE_BELUM]: "bg-slate-100 text-slate-700 border-slate-300",
};

// Nilai khusus dropdown filter (bukan nilai data): dipetakan buildFilterParams ke flag
// backend tanpa_stase / tahap_kosong. Dipakai juga tautan kartu "Perlu dilengkapi" di
// Dashboard (lewat query URL ?stase=__tanpa / ?tahap=__kosong).
const FILTER_TANPA_STASE = "__tanpa";
const FILTER_TAHAP_KOSONG = "__kosong";
const FILTER_DEFAULT = {
    prodi_id: "all", jenjang: "all", tahap: "all", status: "all", stase: "all",
    semester: "all", periode_masuk: "all", biodata: "all",
};

// Filter awal dari query URL (tautan Dashboard), mis. /peserta?status=Aktif&stase=__tanpa.
// Hanya kunci yg dikenal FILTER_DEFAULT yg dipakai; "belum_diisi=1" menyalakan toggle
// "Semester belum diisi".
function filterDariUrl(searchParams) {
    const awal = { ...FILTER_DEFAULT };
    Object.keys(FILTER_DEFAULT).forEach((k) => {
        const v = searchParams.get(k);
        if (v) awal[k] = v;
    });
    // ?biodata=semua|tempat_lahir|... (kartu "Biodata wajib belum lengkap"); nilai lain diabaikan.
    if (!FILTER_BIODATA_NILAI.includes(awal.biodata)) awal.biodata = "all";
    return awal;
}

// Di bawah breakpoint md Tailwind (768px) -- sama dgn batas tampilan ponsel di halaman ini.
const MEDIA_PONSEL = "(max-width: 767px)";

export default function Peserta() {
    const { user } = useAuth();
    const isSuper = seesAllProdi(user.role); // lihat/edit semua prodi (super_admin / admin_ufp)
    const isKoas = isAdminKoas(user.role); // satu Modul Koas (prodi_id), atau lintas SEMUA prodi Koas utk akun lama tanpa modul
    const istilah = istilahProdi(user.role);
    const canDelete = canDeletePeserta(user.role); // khusus Super Admin, lihat lib/roles.js

    const [prodiList, setProdiList] = useState([]);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    // Jaga2 terhadap request yg usang: kalau user ganti filter/halaman sebelum request
    // sebelumnya selesai (mis. request awal tanpa filter yg lambat), respons yg belakangan
    // tiba TAPI berasal dari request yg lebih lama tidak boleh menimpa hasil filter terbaru.
    const requestIdRef = useRef(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [searchParams] = useSearchParams();
    const [filter, setFilter] = useState(() => filterDariUrl(searchParams));
    // Toggle terpisah dari `filter` biasa (bukan dropdown "all"/nilai, cuma nyala/mati) --
    // jumlahnya (belumDiisiCount) mengikuti filter LAIN yg aktif, dihitung server-side.
    const [belumDiisiOnly, setBelumDiisiOnly] = useState(() => searchParams.get("belum_diisi") === "1");
    const [belumDiisiCount, setBelumDiisiCount] = useState(0);
    // Ponsel (< md): dropdown filter dilipat ke panel yg dibuka lewat tombol "Filter"
    // (default tertutup) supaya daftar peserta langsung terlihat. Tablet/desktop tidak
    // terpengaruh -- panelnya `md:contents`, jadi isinya tetap sejajar spt biasa.
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);
    const [isPonsel, setIsPonsel] = useState(() => window.matchMedia(MEDIA_PONSEL).matches);
    useEffect(() => {
        const mq = window.matchMedia(MEDIA_PONSEL);
        const onChange = (e) => setIsPonsel(e.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    // Nama field biodata yang kosong saat submit() terakhir ditolak -- dipakai untuk
    // menyorot Input/Textarea terkait (lihat Field "Tempat Lahir"/"Tanggal Lahir"/dst).
    const [bioInvalid, setBioInvalid] = useState([]);
    const [rscmInfo, setRscmInfo] = useState(null);
    const [periodeCuti, setPeriodeCuti] = useState(null); // null = belum dimuat dari GET /peserta/{id}
    const [staseTerkunci, setStaseTerkunci] = useState(false);
    const [rscmDraft, setRscmDraft] = useState("");
    const [rscmBusy, setRscmBusy] = useState(false);
    const [editing, setEditing] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [periode, setPeriode] = useState(null);

    // Bulk edit
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    // Audit performa 28 Sep 2026 (temuan #10) -- mode "Pilih Semua N peserta yang
    // sesuai filter" TIDAK LAGI memuat/menyimpan ribuan ID di frontend (dulu lewat
    // /peserta/npm-lookup lalu dieksplosikan ke selectedIds). Sekarang cukup flag
    // ini + filter yang aktif dikirim APA ADANYA ke backend (bulk-update, semester-
    // massal, kirim ke Orientasi RS) -- backend yang mencari & mengeksekusi
    // langsung. selectedIds TETAP dipakai spt biasa utk centang manual per-baris
    // & "Pilih via Daftar NPM" (TIDAK berubah) -- keduanya saling eksklusif dgn
    // mode ini (interaksi manual apa pun otomatis mematikan mode filter, lihat
    // toggleRow/toggleAll/applyNpmPicker/clearSelection).
    const [selectAllFilterActive, setSelectAllFilterActive] = useState(false);
    const [bulkField, setBulkField] = useState(null);       // "status_keaktifan" | "tahap" | "stase" | "prodi_id" | null
    const [bulkValue, setBulkValue] = useState("");
    const [bulkConfirm, setBulkConfirm] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const isProdiBulk = bulkField === "prodi_id";

    // Kirim ke Orientasi RS -- terpisah dari mekanisme "Ubah <field>" di atas (bukan
    // update field, tapi bikin entri baru di collection orientasi_rs), tapi gaya
    // tombol/dialog-nya sengaja disamakan dgn bulk edit yg sudah ada.
    const [kirimOrientasiOpen, setKirimOrientasiOpen] = useState(false);
    const [kirimOrientasiBusy, setKirimOrientasiBusy] = useState(false);

    // Ubah Semester massal -- dialog terpisah dari mekanisme "Ubah <field>" (BULK_FIELDS)
    // krn 2 field independen (Semester & Periode Masuk, masing2 boleh "tidak diubah").
    const [semesterBulkOpen, setSemesterBulkOpen] = useState(false);
    const [semesterBulkSemester, setSemesterBulkSemester] = useState("__skip__");
    const [semesterBulkPeriode, setSemesterBulkPeriode] = useState("__skip__");
    const [semesterBulkBusy, setSemesterBulkBusy] = useState(false);

    // Pilih peserta via daftar NPM (opsi B, dipakai terutama untuk "Ubah Prodi").
    // Dicocokkan dari npmLookup (hasil filter SAAT INI, tanpa pagination) -- bukan
    // dari `items` yg cuma berisi satu halaman -- supaya tetap bisa pilih lintas halaman.
    const [npmPickerOpen, setNpmPickerOpen] = useState(false);
    const [npmPickerText, setNpmPickerText] = useState("");
    const [npmNotFound, setNpmNotFound] = useState([]);
    const [npmLookup, setNpmLookup] = useState([]);
    const [npmLookupLoading, setNpmLookupLoading] = useState(false);

    const locked = !!periode?.is_locked; // admin_prodi & di luar periode edit
    const canBulk = !locked;
    const selectedCount = selectAllFilterActive ? total : selectedIds.size;

    const loadProdi = async () => {
        try { const { data } = await api.get("/prodi"); setProdiList(data); } catch {}
    };
    const loadPeriode = async () => {
        try { const { data } = await api.get("/periode-config"); setPeriode(data); } catch {}
    };

    const buildFilterParams = () => {
        const params = { q: q || undefined };
        if (isSuper && filter.prodi_id !== "all") params.prodi_id = filter.prodi_id;
        if (filter.jenjang !== "all") params.jenjang = filter.jenjang;
        if (filter.tahap === FILTER_TAHAP_KOSONG) params.tahap_kosong = true;
        else if (filter.tahap !== "all") params.tahap = filter.tahap;
        if (filter.status !== "all") params.status = filter.status;
        if (filter.stase === FILTER_TANPA_STASE) params.tanpa_stase = true;
        else if (filter.stase !== "all") params.stase = filter.stase;
        if (filter.semester !== "all") params.semester = filter.semester;
        if (filter.biodata !== "all") params.biodata_kosong = filter.biodata;
        if (filter.periode_masuk !== "all") {
            const opt = PERIODE_MASUK_OPTIONS.find((o) => o.value === filter.periode_masuk);
            if (opt) { params.periode = opt.periode; params.tahun_akademik_mulai = opt.tahun; }
        }
        return params;
    };

    // Dihitung terpisah dari `load()` -- selalu mencerminkan "berapa yg belum diisi
    // KALAU toggle ini dinyalakan sekarang", independen dari status toggle itu sendiri.
    const loadBelumDiisiCount = async () => {
        try {
            const { data } = await api.get("/peserta", {
                params: { ...buildFilterParams(), belum_diisi_semester: true, page_size: 1 },
            });
            setBelumDiisiCount(data.total);
        } catch {}
    };

    const load = async (pageArg) => {
        const targetPage = pageArg ?? page;
        const reqId = ++requestIdRef.current;
        setLoading(true);
        try {
            const params = { ...buildFilterParams(), page: targetPage, page_size: PAGE_SIZE };
            if (belumDiisiOnly) params.belum_diisi_semester = true;
            const { data } = await api.get("/peserta", { params });
            if (reqId !== requestIdRef.current) return; // respons request lama yg sudah usang, abaikan
            setItems(data.items);
            setTotal(data.total);
            setSelectedIds(new Set());
            // Selalu ikut refresh di sini (bukan cuma saat filter berubah) -- supaya
            // angkanya benar setelah PERUBAHAN DATA apa pun (tambah/edit/hapus/bulk/
            // import), bukan cuma saat filter/halaman berganti. Baris data peserta yg
            // berubah tidak selalu ada di `data.items` (bisa di halaman lain), jadi
            // tidak bisa dihitung dari situ -- perlu query terpisah ke server.
            loadBelumDiisiCount();
        } catch {
            if (reqId !== requestIdRef.current) return;
            toast.error("Gagal memuat data");
        } finally {
            if (reqId === requestIdRef.current) setLoading(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const goToPage = (p) => {
        const clamped = Math.min(Math.max(1, p), totalPages);
        if (clamped === page) return;
        setPage(clamped);
        load(clamped);
    };

    const toggleRow = (id) => {
        setSelectAllFilterActive(false); // interaksi manual -> keluar dari mode "semua sesuai filter"
        setSelectedIds((prev) => {
            const n = new Set(prev);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    };
    const toggleAll = () => {
        setSelectAllFilterActive(false);
        setSelectedIds((prev) => prev.size === items.length ? new Set() : new Set(items.map((i) => i.id)));
    };
    const clearSelection = () => { setSelectedIds(new Set()); setSelectAllFilterActive(false); setNpmNotFound([]); };

    // Bangun objek filter yg dikirim ke backend saat mode "Pilih Semua sesuai
    // filter" aktif -- field-nya PERSIS sama dgn PesertaFilterIn di server.py.
    const buildBulkFilterPayload = () => ({ ...buildFilterParams(), belum_diisi_semester: belumDiisiOnly });

    // "Centang semua" di header cuma pilih SATU HALAMAN (items = baris halaman ini
    // saja, krn daftar dipaginasi). Tombol "Pilih semua N peserta yang sesuai
    // filter" (muncul kalau hasil filter > 1 halaman) TIDAK LAGI memuat daftar ID
    // (audit performa 28 Sep 2026, temuan #10) -- `total` sudah tersedia dari
    // GET /peserta yg baru saja dimuat, jadi tinggal set flag, TANPA panggilan API
    // apa pun. Aksi massal (bulk-update/semester-massal/kirim-orientasi) yg
    // membaca flag ini akan kirim `filter` (bukan `ids`) ke backend -- backend yg
    // mencari & mengeksekusi langsung.
    const selectAllMatchingFilter = () => {
        setSelectedIds(new Set());
        setSelectAllFilterActive(true);
        toast.success(`${total} peserta dipilih (semua yang sesuai filter saat ini)`);
    };

    const openBulk = (field) => { setBulkField(field); setBulkValue(""); };

    // Muat daftar id/npm/nama ringan (TANPA pagination, sesuai filter aktif) khusus
    // utk dialog "Pilih via Daftar NPM" -- supaya bisa mencocokkan NPM lintas halaman.
    const openNpmPicker = async () => {
        setNpmPickerText("");
        setNpmNotFound([]);
        setNpmPickerOpen(true);
        setNpmLookupLoading(true);
        try {
            const { data } = await api.get("/peserta/npm-lookup", { params: buildFilterParams() });
            setNpmLookup(data);
        } catch {
            toast.error("Gagal memuat daftar NPM");
            setNpmLookup([]);
        } finally {
            setNpmLookupLoading(false);
        }
    };

    // Opsi B: pilih baris (sesuai filter saat ini, lintas halaman) berdasarkan daftar NPM yang di-paste.
    const applyNpmPicker = () => {
        const wanted = [...new Set(
            npmPickerText.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean)
        )];
        if (wanted.length === 0) {
            toast.error("Masukkan minimal satu NPM");
            return;
        }
        const byNpm = new Map(npmLookup.map((i) => [String(i.npm ?? "").trim(), i]));
        const foundIds = new Set();
        const notFound = [];
        wanted.forEach((npm) => {
            const row = byNpm.get(npm);
            if (row) foundIds.add(row.id);
            else notFound.push(npm);
        });
        setSelectAllFilterActive(false); // pilih via daftar NPM = seleksi eksplisit, bukan "semua sesuai filter"
        setSelectedIds(foundIds);
        setNpmNotFound(notFound);
        setNpmPickerOpen(false);
        if (foundIds.size === 0) {
            toast.error(`Tidak ada NPM yang cocok dari ${wanted.length} NPM (periksa filter tabel saat ini).`);
        } else {
            toast.success(
                `${foundIds.size} peserta dipilih dari ${wanted.length} NPM` +
                (notFound.length ? ` (${notFound.length} tidak ditemukan)` : "")
            );
        }
    };

    // Body seleksi utk endpoint aksi massal -- `filter` (mode "Pilih Semua sesuai
    // filter", audit performa 28 Sep 2026 temuan #10) ATAU `ids` (centang manual/
    // paste NPM, TIDAK BERUBAH), TIDAK PERNAH keduanya sekaligus.
    const buildSelectionPayload = () =>
        selectAllFilterActive ? { filter: buildBulkFilterPayload() } : { ids: [...selectedIds] };

    const applyBulk = async () => {
        if (!bulkField || !bulkValue) return;
        setBulkBusy(true);
        try {
            const { data } = await api.put("/peserta/bulk-update", {
                ...buildSelectionPayload(),
                field: bulkField,
                value: bulkValue,
            });
            setBulkResult(data);
            setBulkField(null);
            setBulkConfirm(false);
            setNpmNotFound([]);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setBulkBusy(false);
        }
    };

    const doKirimOrientasi = async () => {
        setKirimOrientasiBusy(true);
        try {
            const selection = selectAllFilterActive ? { filter: buildBulkFilterPayload() } : { peserta_ids: [...selectedIds] };
            const { data } = await api.post("/orientasi-rs/bulk-create-from-peserta", selection);
            if (data.created > 0) {
                toast.success(`${data.created} peserta dikirim ke Orientasi RS.`);
            }
            if (data.skipped.length > 0) {
                toast.error(`${data.skipped.length} peserta dilewati (mis. bukan status MDU/MDK, atau sudah ada entri Orientasi yang masih berjalan).`);
            }
            setKirimOrientasiOpen(false);
            clearSelection();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setKirimOrientasiBusy(false);
        }
    };

    const closeSemesterBulk = () => {
        setSemesterBulkOpen(false);
        setSemesterBulkSemester("__skip__");
        setSemesterBulkPeriode("__skip__");
    };

    const applySemesterBulk = async () => {
        const body = { ...buildSelectionPayload() };
        if (semesterBulkSemester !== "__skip__") body.semester = semesterBulkSemester;
        if (semesterBulkPeriode !== "__skip__") {
            const opt = PERIODE_MASUK_OPTIONS.find((o) => o.value === semesterBulkPeriode);
            body.periode = opt.periode;
            body.tahun_akademik_mulai = opt.tahun;
        }
        setSemesterBulkBusy(true);
        try {
            const { data } = await api.put("/peserta/semester-massal", body);
            let msg = `${data.updated} diperbarui`;
            if (data.dilewati_status > 0) msg += `, ${data.dilewati_status} dilewati karena Lulus/Meninggal`;
            if (data.failed.length > 0) msg += `, ${data.failed.length} gagal`;
            if (data.failed.length > 0) toast.error(msg); else toast.success(msg);
            closeSemesterBulk();
            clearSelection();
            load(); // load() sudah selalu ikut refresh loadBelumDiisiCount() di akhir
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSemesterBulkBusy(false);
        }
    };

    useEffect(() => { loadProdi(); loadPeriode(); }, []);
    // Filter/search berubah -> selalu kembali ke halaman 1 (jangan pakai state `page`
    // yg lama di request ini, supaya tidak sempat fetch dgn kombinasi page+filter basi).
    useEffect(() => { setPage(1); load(1); /* eslint-disable-line */ }, [
        q, filter.prodi_id, filter.jenjang, filter.tahap, filter.status, filter.stase,
        filter.semester, filter.periode_masuk, filter.biodata, belumDiisiOnly,
    ]);
    // Catatan: indikator "Belum diisi (N)" TIDAK punya useEffect terpisah lagi --
    // load() di atas sudah selalu memanggil loadBelumDiisiCount() di akhir, jadi
    // otomatis ikut ter-refresh setiap kali filter berubah (via effect load(1) di
    // bawah) MAUPUN setelah tambah/edit/hapus/bulk/import (semua jalur itu sudah
    // memanggil load()). Efek terpisah sebelumnya jadi duplikat & sudah dihapus.

    const prodiMap = useMemo(() => {
        const m = {};
        prodiList.forEach((p) => (m[p.id] = p));
        return m;
    }, [prodiList]);

    // Role yg terikat 1 jenjang tetap (admin_prodi -> jenjang prodinya sendiri,
    // admin_koas -> selalu "Koas") vs role bebas (isSuper -- admin_ufp/super_admin,
    // perilaku filter Jenjang/Tahap utk role ini TIDAK berubah sama sekali).
    const isJenjangLocked = !isSuper;
    const lockedJenjang = isKoas ? "Koas" : (isJenjangLocked ? prodiMap[user.prodi_id]?.jenjang : null);
    // admin_koas selalu langsung "diketahui" (tidak bergantung prodiList). admin_prodi
    // baru "diketahui" setelah prodiList termuat & memuat prodi user ybs -- SEBELUM itu,
    // filter Jenjang ditahan netral/disabled (bukan ditampilkan salah) & kontrol Tahap
    // disembunyikan dulu (bukan sempat tampil lalu hilang).
    const jenjangKnown = !isJenjangLocked || !!lockedJenjang;
    // Pilihan Stase filter & Ubah massal: "Belum Stase" tidak ditawarkan bila jenjang tampilan = Koas.
    const opsiStaseFilter = opsiStase(isJenjangLocked ? lockedJenjang : filter.jenjang);
    // Tahap cuma berlaku utk Sp1 -- utk role bebas (isSuper) TETAP selalu tampil
    // spt sebelumnya (tabel boleh berisi campuran jenjang utk mereka).
    const showTahapControls = !isJenjangLocked || (jenjangKnown && lockedJenjang === "Sp1");
    // Badge tombol "Filter" (ponsel): hanya filter yg memang tampil & bisa diubah user ini.
    const activeFilterCount = [
        isSuper && filter.prodi_id !== "all",
        !isJenjangLocked && filter.jenjang !== "all",
        showTahapControls && filter.tahap !== "all",
        filter.status !== "all",
        filter.stase !== "all",
        filter.semester !== "all",
        filter.periode_masuk !== "all",
        filter.biodata !== "all",
        belumDiisiOnly,
    ].filter(Boolean).length;

    // Gabungan halaman saat ini (`items`) + hasil npm-lookup, dipakai utk preview
    // "Ubah Prodi" massal supaya tetap benar walau id yg terpilih berasal dari
    // halaman lain (dipilih lewat dialog "Pilih via Daftar NPM").
    const selectionLookup = useMemo(() => {
        const m = new Map();
        npmLookup.forEach((i) => m.set(i.id, i));
        items.forEach((i) => m.set(i.id, i));
        return m;
    }, [npmLookup, items]);
    const selectedRows = useMemo(
        () => [...selectedIds].map((id) => selectionLookup.get(id)).filter(Boolean),
        [selectedIds, selectionLookup]
    );
    // Breakdown Sp1/Sp2/Koas dari baris yg dipilih -- kategori Orientasi RS
    // (Spesialis/Subspesialis/Koas) 1-ke-1 dgn jenjang, jadi cukup ditampilkan sbg
    // info, tidak perlu admin pilih manual (lihat backend _kategori_from_jenjang).
    const kirimOrientasiBreakdown = useMemo(() => {
        let sp1 = 0, sp2 = 0, koas = 0;
        selectedRows.forEach((r) => {
            if (r.jenjang === "Sp1") sp1++;
            else if (r.jenjang === "Sp2") sp2++;
            else if (r.jenjang === "Koas") koas++;
        });
        return { sp1, sp2, koas };
    }, [selectedRows]);

    const openAdd = () => {
        setEditing(null);
        const initial = { ...EMPTY };
        if (isKoas) {
            // Jenjang diprefill & dikunci "Koas". Bermodul: Modul terisi & dikunci (server juga memaksanya);
            // akun lama tanpa modul: dipilih dari antara prodi berjenjang Koas (availableProdi).
            initial.jenjang = "Koas";
            if (user.prodi_id) initial.prodi_id = user.prodi_id;
        } else if (!isSuper && user.prodi_id) {
            const p = prodiMap[user.prodi_id];
            initial.prodi_id = user.prodi_id;
            if (p) initial.jenjang = p.jenjang;
        }
        setForm(initial);
        setBioInvalid([]);
        setDialogOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setForm({ ...EMPTY, ...row, tanggal_lahir: row.tanggal_lahir || "" });
        setBioInvalid([]);
        setDialogOpen(true);
        setRscmInfo(null);
        setRscmDraft("");
        setPeriodeCuti(null);
        setStaseTerkunci(!!row.stase_otomatis_nonaktif);
        // GET /peserta (daftar) sengaja tidak mengirim NIK (F-11/F-15) -- diambil
        // terpisah di sini, satu-satunya jalur yang mengembalikan NIK lengkap.
        // Sekalian ambil field identitas WNA dari sumber yg sama supaya konsisten;
        // data lama (blm punya field ini) otomatis fallback WNI+NIK.
        api.get(`/peserta/${row.id}`)
            .then(({ data }) => {
                setForm((f) => ({
                    ...f,
                    nik: data.nik || "",
                    kewarganegaraan: data.kewarganegaraan || "WNI",
                    jenis_identitas: data.jenis_identitas || "NIK",
                    negara_penerbit_paspor: data.negara_penerbit_paspor || "",
                }));
                setPeriodeCuti(data.periode_cuti || []);
                setStaseTerkunci(!!data.stase_otomatis_nonaktif);
            })
            .catch(() => {});
        api.get(`/peserta/${row.id}/unit-cost`)
            .then(({ data }) => { setRscmInfo(data); setRscmDraft(String(data.hari_rscm_final ?? "")); })
            .catch(() => setRscmInfo(null));
    };

    const saveRscm = async (reset) => {
        if (!editing) return;
        setRscmBusy(true);
        try {
            const body = reset ? { hari_rscm_reset: true } : { hari_rscm_terhitung: Number(rscmDraft) };
            await api.put(`/peserta/${editing.id}`, body);
            toast.success(reset ? "Dikembalikan ke hitungan otomatis" : "Koreksi hari Stase RSCM disimpan");
            const { data } = await api.get(`/peserta/${editing.id}/unit-cost`);
            setRscmInfo(data);
            setRscmDraft(String(data.hari_rscm_final ?? ""));
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setRscmBusy(false);
        }
    };

    const wniForm = (form.kewarganegaraan || "WNI") !== "WNA";
    // Saat edit, nomor lama yg tidak diubah tidak ditandai (tidak menghalangi simpan perubahan lain).
    const galatNoHp = galatNoHpForm(form.no_hp, form.kewarganegaraan, editing ? editing.no_hp : undefined);
    const ubahNoHp = (nilai) => {
        setForm({ ...form, no_hp: nilai });
        setBioInvalid((b) => b.filter((k) => k !== "no_hp"));
    };

    const submit = async () => {
        const payload = { ...form };
        if (payload.jenjang !== "Sp1") delete payload.tahap;
        else if (!payload.tahap) payload.tahap = "1";
        Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
        // Field unit cost dikelola terpisah (bukan lewat form utama)
        ["hari_rscm_terhitung", "hari_rscm_reset", "hari_rscm_sumber",
         "hari_rscm_dikoreksi_oleh", "hari_rscm_dikoreksi_pada", "hari_rscm_periode"].forEach((k) => delete payload[k]);
        delete payload.periode_cuti; // disimpan terpisah lewat bagian Periode Cuti (PUT /peserta/{id}/periode-cuti)
        delete payload.stase_otomatis_nonaktif; // disimpan terpisah lewat pengalih Kunci Stase (PUT /peserta/{id}/kunci-stase)
        // required fields
        if (!payload.nama || !payload.npm || !payload.nik || !payload.email || !payload.prodi_id) {
            return toast.error(`Lengkapi field wajib: Nama, NPM, ${payload.jenis_identitas === "Paspor" ? "Nomor Paspor" : "NIK"}, Email, ${istilah.singkat}`);
        }
        if (payload.jenis_identitas === "Paspor" && !payload.negara_penerbit_paspor) {
            return toast.error("Negara Penerbit Paspor wajib diisi untuk Jenis Identitas = Paspor");
        }
        if (!STATUS_TANPA_STASE.includes(payload.status_keaktifan) && !payload.stase) {
            return toast.error("Stase wajib diisi kecuali Status Keaktifan = Lulus / Meninggal / Cuti / Tidak Aktif");
        }
        if (!STATUS_TANPA_SEMESTER_WAJIB.includes(payload.status_keaktifan) && (!payload.semester || !payload.periode || payload.tahun_akademik_mulai == null)) {
            return toast.error("Semester & Periode Masuk wajib diisi kecuali Status Keaktifan = Lulus / Meninggal");
        }
        // Biodata wajib (Tempat Lahir, Tanggal Lahir, No. HP, Alamat) -- kecuali status
        // akhir Lulus/Meninggal (data historis, sama dgn pengecualian Semester di atas).
        if (!STATUS_TANPA_SEMESTER_WAJIB.includes(payload.status_keaktifan)) {
            const biodataFields = [
                ["tempat_lahir", "Tempat Lahir"],
                ["tanggal_lahir", "Tanggal Lahir"],
                ["no_hp", "No. HP"],
                ["alamat", "Alamat"],
            ];
            const kosong = biodataFields.filter(([key]) => !String(form[key] || "").trim());
            if (kosong.length) {
                setBioInvalid(kosong.map(([key]) => key));
                return toast.error(
                    `Tempat Lahir, Tanggal Lahir, No. HP, dan Alamat wajib diisi (kecuali status Lulus/Meninggal): belum terisi -> ${kosong.map(([, label]) => label).join(", ")}`
                );
            }
        }
        const galatHp = galatNoHpForm(form.no_hp, form.kewarganegaraan, editing ? editing.no_hp : undefined);
        if (galatHp) {
            setBioInvalid(["no_hp"]);
            return toast.error(galatHp);
        }
        setBioInvalid([]);
        try {
            if (editing) await api.put(`/peserta/${editing.id}`, payload);
            else await api.post("/peserta", payload);
            toast.success(editing ? "Data diperbarui" : "Peserta didik ditambahkan");
            setDialogOpen(false);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/peserta/${confirmDelete.id}`);
            toast.success("Peserta dihapus");
            setConfirmDelete(null);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const download = async (fmt) => {
        try {
            const params = {};
            if (isSuper && filter.prodi_id !== "all") params.prodi_id = filter.prodi_id;
            if (filter.jenjang !== "all") params.jenjang = filter.jenjang;
            // Ekspor hanya mendukung tahap bernilai; "Tahap belum diisi" tidak diteruskan.
            if (filter.tahap !== "all" && filter.tahap !== FILTER_TAHAP_KOSONG) params.tahap = filter.tahap;
            params.halaman = "peserta";
            const res = await api.get(`/export/${fmt}`, { params, responseType: "blob" });
            downloadBlobResponse(res, `aura_peserta.${fmt === "excel" ? "xlsx" : "pdf"}`);
        } catch { toast.error("Gagal export"); }
    };

    const availableProdi = form.jenjang
        ? prodiList.filter((p) => p.jenjang === form.jenjang)
        : prodiList;

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 space-y-4">
            <StickyHeader>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Data Master</div>
                    <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                        Peserta Didik
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        {isSuper ? "Semua prodi (baca & tulis)." : isKoas && !user.prodi_id ? "Peserta Koas (semua modul Koas)." : `${istilah.singkat} anda: ${prodiMap[user.prodi_id]?.nama_prodi || "-"}`}
                    </p>
                </div>
                <div className="flex flex-wrap gap-1.5 md:gap-2">
                    <Button variant="outline" onClick={() => setImportOpen(true)} disabled={locked} data-testid="peserta-import-button" className="border-teal-300 text-teal-800 hover:bg-teal-50">
                        <Upload className="w-4 h-4 mr-2" /> Import Excel
                    </Button>
                    <Button asChild variant="outline" data-testid="peserta-import-history-button">
                        <Link to="/peserta/riwayat-import"><History className="w-4 h-4 mr-2" /> Riwayat Import</Link>
                    </Button>
                    {isSuperAdmin(user.role) && (
                        <Button asChild variant="outline" data-testid="peserta-terhapus-button">
                            <Link to="/peserta/terhapus"><Archive className="w-4 h-4 mr-2" /> Peserta Terhapus</Link>
                        </Button>
                    )}
                    <Button variant="outline" onClick={() => download("excel")} data-testid="peserta-export-excel">
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
                    </Button>
                    <Button variant="outline" onClick={() => download("pdf")} data-testid="peserta-export-pdf">
                        <FileText className="w-4 h-4 mr-2" /> PDF
                    </Button>
                    <Button onClick={() => openAdd()} disabled={locked || !jenjangKnown} data-testid="ppds-add-student-button" className="bg-[#00B9AD] hover:bg-[#60C0D0]">
                        <Plus className="w-4 h-4 mr-2" /> Tambah Peserta
                    </Button>
                </div>
            </div>

            {locked && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="peserta-locked-banner">
                    <Lock className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-sm text-amber-800">
                        <div className="font-bold">Periode input bulan ini sudah ditutup (tanggal {teksRentangPeriode(periode)}).</div>
                        Data akan bisa diedit lagi mulai tanggal {periode.tanggal_mulai} bulan depan. Saat ini Anda tetap
                        bisa melihat dan meng-export data, tetapi tidak bisa menambah, mengubah, menghapus, atau meng-import.
                    </div>
                </div>
            )}

            <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-3 md:p-4 flex flex-col md:flex-row md:flex-wrap gap-2 items-start md:items-center">
                    <div className="flex w-full gap-2 md:contents">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            data-testid="ppds-table-search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={isPonsel ? "Cari nama / NPM / NIK" : "Cari nama / NPM / NIK / email / teks lama..."}
                            className="pl-9 pr-2 md:pr-3 h-10"
                        />
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        className={`md:hidden h-10 shrink-0 gap-1 px-2.5 ${filterPanelOpen ? "border-[var(--brand-turq-text)] text-[var(--brand-turq-text)]" : "text-slate-700"}`}
                        onClick={() => setFilterPanelOpen((v) => !v)}
                        aria-expanded={filterPanelOpen}
                        aria-controls="ppds-filter-panel"
                        data-testid="ppds-filter-panel-toggle"
                    >
                        <SlidersHorizontal className="w-4 h-4" />
                        {/* < 410px: label disembunyikan dari tampilan (tetap terbaca pembaca layar)
                            supaya placeholder kolom cari tidak terpotong, juga saat badge tampil. */}
                        <span className="max-[409px]:sr-only">Filter</span>
                        {activeFilterCount > 0 && (
                            <span className="min-w-5 h-5 px-1.5 rounded-full bg-[#267B84] text-white text-[11px] font-bold leading-5 text-center" data-testid="ppds-filter-active-count">
                                {activeFilterCount}
                            </span>
                        )}
                    </Button>
                    </div>
                    <div
                        id="ppds-filter-panel"
                        className={`${filterPanelOpen ? "grid" : "hidden"} w-full grid-cols-2 gap-2 md:contents`}
                        data-testid="ppds-filter-panel"
                    >
                    {isSuper && (
                        <ProdiSelect
                            prodi={prodiList}
                            value={filter.prodi_id}
                            onValueChange={(v) => setFilter({ ...filter, prodi_id: v })}
                            includeAll
                            placeholder="Prodi"
                            className="col-span-2 w-full md:w-56 h-10 flex-shrink-0"
                            testId="ppds-filter-prodi-select"
                        />
                    )}
                    <Select
                        value={isJenjangLocked ? (lockedJenjang || "all") : filter.jenjang}
                        onValueChange={(v) => setFilter({ ...filter, jenjang: v })}
                        disabled={isJenjangLocked}
                    >
                        <SelectTrigger className="w-full md:w-40 h-10 flex-shrink-0" data-testid="ppds-filter-jenjang-select"><SelectValue placeholder="Jenjang" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua Jenjang</SelectItem>
                            <SelectItem value="Sp1">Sp1</SelectItem>
                            <SelectItem value="Sp2">Sp2</SelectItem>
                            <SelectItem value="Koas">Koas</SelectItem>
                        </SelectContent>
                    </Select>
                    {showTahapControls && (
                        <Select value={filter.tahap} onValueChange={(v) => setFilter({ ...filter, tahap: v })}>
                            <SelectTrigger className="w-full md:w-40 h-10 flex-shrink-0" data-testid="ppds-filter-tahap-select"><SelectValue placeholder="Tahap" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua Tahap</SelectItem>
                                <SelectItem value="1">Tahap 1</SelectItem>
                                <SelectItem value="2">Tahap 2</SelectItem>
                                <SelectItem value="3">Tahap 3</SelectItem>
                                <SelectItem value="MDU/MDK">MDU/MDK</SelectItem>
                                <SelectItem value={FILTER_TAHAP_KOSONG}>Tahap belum diisi</SelectItem>
                            </SelectContent>
                        </Select>
                    )}
                    <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
                        <SelectTrigger className="w-full md:w-40 h-10 flex-shrink-0"><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua Status</SelectItem>
                            <SelectItem value="Aktif">Aktif</SelectItem>
                            <SelectItem value="Cuti">Cuti</SelectItem>
                            <SelectItem value="Lulus">Lulus</SelectItem>
                            <SelectItem value="Tidak Aktif">Tidak Aktif</SelectItem>
                            <SelectItem value="Meninggal">Meninggal</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={filter.stase} onValueChange={(v) => setFilter({ ...filter, stase: v })}>
                        <SelectTrigger className="w-full md:w-40 h-10 flex-shrink-0"><SelectValue placeholder="Stase" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua Stase</SelectItem>
                            {opsiStaseFilter.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            <SelectItem value={FILTER_TANPA_STASE}>Tanpa stase</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={filter.semester} onValueChange={(v) => setFilter({ ...filter, semester: v })}>
                        <SelectTrigger className="w-full md:w-44 h-10 flex-shrink-0" data-testid="ppds-filter-semester-select"><SelectValue placeholder="Semester" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua Semester</SelectItem>
                            {SEMESTER_OPTIONS.map((n) => (
                                <SelectItem key={n} value={String(n)}>Semester {n}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={filter.periode_masuk} onValueChange={(v) => setFilter({ ...filter, periode_masuk: v })}>
                        <SelectTrigger className="w-full md:w-48 h-10 flex-shrink-0" data-testid="ppds-filter-periode-masuk-select"><SelectValue placeholder="Periode Masuk" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua Periode</SelectItem>
                            {PERIODE_MASUK_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={filter.biodata} onValueChange={(v) => setFilter({ ...filter, biodata: v })}>
                        <SelectTrigger className="w-full md:w-52 h-10 flex-shrink-0" data-testid="ppds-filter-biodata-select"><SelectValue placeholder="Biodata" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua biodata</SelectItem>
                            <SelectItem value={FILTER_BIODATA_SEMUA}>Biodata belum lengkap</SelectItem>
                            <SelectItem value={FILTER_BIODATA_IDENTITAS}>NIK atau Email kosong</SelectItem>
                            {BIODATA_PENANDA.map((b) => (
                                <SelectItem key={b.key} value={b.key}>{b.label} kosong</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button
                        size="sm"
                        variant="outline"
                        className={`col-span-2 h-10 whitespace-nowrap ${belumDiisiOnly ? "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100" : "text-slate-600"}`}
                        onClick={() => setBelumDiisiOnly((v) => !v)}
                        data-testid="ppds-filter-belum-diisi-toggle"
                    >
                        Semester belum diisi ({belumDiisiCount})
                    </Button>
                    </div>
                </CardContent>
            </Card>

            {isSuper && canBulk && (
                <div className="flex justify-end -mt-1">
                    <Button
                        size="sm" variant="outline"
                        className="border-teal-300 text-teal-800 hover:bg-teal-50"
                        onClick={openNpmPicker}
                        data-testid="npm-picker-open"
                    >
                        <ListChecks className="w-4 h-4 mr-2" /> Pilih via Daftar NPM
                    </Button>
                </div>
            )}

            {canBulk && selectedCount > 0 && (
                <div className="rounded-lg border border-[#267B84] bg-[#267B84] text-white shadow-lg px-4 py-3 flex flex-wrap items-center gap-3" data-testid="peserta-bulk-bar">
                    {/* Teal cuma 4.94:1 dgn putih -> label teks solid (bukan di-dim), dan overlay
                        tombol pakai bg-black/NN (menggelapkan) bukan bg-white/NN (menerangkan,
                        jatuh ke ~4.1:1 dan gagal AA). */}
                    <span className="font-bold text-sm">{selectedCount} peserta dipilih</span>
                    {total > items.length && selectedCount < total && (
                        <button
                            type="button"
                            className="text-xs underline text-white/90 hover:text-white disabled:opacity-50"
                            onClick={selectAllMatchingFilter}
                            data-testid="select-all-matching-filter"
                        >
                            {`Pilih semua ${total} peserta yang sesuai filter`}
                        </button>
                    )}
                    <span className="text-white">|</span>
                    <span className="text-xs text-white">Ubah untuk semua yang dipilih:</span>
                    <Button size="sm" variant="outline" className="bg-black/10 border-white/30 text-white hover:bg-black/20 hover:text-white" onClick={() => openBulk("status_keaktifan")} data-testid="bulk-status">
                        Ubah Status
                    </Button>
                    {showTahapControls && (
                        <Button size="sm" variant="outline" className="bg-black/10 border-white/30 text-white hover:bg-black/20 hover:text-white" onClick={() => openBulk("tahap")} data-testid="bulk-tahap">
                            Ubah Tahap
                        </Button>
                    )}
                    <Button size="sm" variant="outline" className="bg-black/10 border-white/30 text-white hover:bg-black/20 hover:text-white" onClick={() => openBulk("stase")} data-testid="bulk-stase">
                        Ubah Stase
                    </Button>
                    {isSuper && (
                        <Button size="sm" variant="outline" className="bg-black/10 border-white/30 text-white hover:bg-black/20 hover:text-white" onClick={() => openBulk("prodi_id")} data-testid="bulk-prodi">
                            Ubah Prodi
                        </Button>
                    )}
                    <Button size="sm" variant="outline" className="bg-black/10 border-white/30 text-white hover:bg-black/20 hover:text-white" onClick={() => setSemesterBulkOpen(true)} data-testid="bulk-semester">
                        Ubah Semester
                    </Button>
                    <Button size="sm" variant="outline" className="bg-black/10 border-white/30 text-white hover:bg-black/20 hover:text-white" onClick={() => setKirimOrientasiOpen(true)} data-testid="bulk-kirim-orientasi">
                        Kirim ke Orientasi RS
                    </Button>
                    <Button size="sm" variant="ghost" className="text-white hover:bg-black/10 ml-auto" onClick={clearSelection} data-testid="bulk-cancel">
                        <X className="w-4 h-4 mr-1" /> Batal
                    </Button>
                </div>
            )}
            </StickyHeader>

            <Card className="border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full compact">
                        <thead className="bg-slate-100">
                            <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                {canBulk && (
                                    <th className="w-10 pl-4">
                                        <Checkbox
                                            checked={selectAllFilterActive || (items.length > 0 && selectedCount === items.length)
                                                ? true : selectedCount > 0 ? "indeterminate" : false}
                                            onCheckedChange={toggleAll}
                                            aria-label="Pilih semua"
                                            data-testid="peserta-select-all"
                                        />
                                    </th>
                                )}
                                <th>Nama</th>
                                <th>NPM</th>
                                <th>{istilah.singkat}</th>
                                <th>Jenjang</th>
                                <th>Tahap / Semester</th>
                                {/* Teks lama (arsip baca-saja) -- disembunyikan di layar < 1024px supaya tabel tidak sesak. */}
                                <th className="hidden lg:table-cell">Teks lama</th>
                                <th>Status</th>
                                <th>Stase</th>
                                <th>Diubah</th>
                                <th className="text-right pr-4">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={canBulk ? 11 : 10} className="text-center py-10 text-slate-400">Memuat...</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={canBulk ? 11 : 10} className="text-center py-10 text-slate-400">Belum ada data. Klik "Tambah Peserta" untuk mulai.</td></tr>
                            ) : items.map((r) => (
                                <tr key={r.id} className={`border-t border-slate-200 hover:bg-slate-50 ${(selectAllFilterActive || selectedIds.has(r.id)) ? "bg-sky-50" : ""}`}>
                                    {canBulk && (
                                        <td className="pl-4">
                                            <Checkbox
                                                checked={selectAllFilterActive || selectedIds.has(r.id)}
                                                onCheckedChange={() => toggleRow(r.id)}
                                                aria-label={`Pilih ${r.nama}`}
                                                data-testid={`peserta-select-${r.id}`}
                                            />
                                        </td>
                                    )}
                                    <td className="font-semibold text-slate-800">{r.nama}</td>
                                    <td className="font-mono text-xs">{r.npm}</td>
                                    <td className="text-slate-600">{prodiMap[r.prodi_id]?.nama_prodi || "-"}</td>
                                    <td><Badge variant="outline" className="text-[10px]">{r.jenjang}</Badge></td>
                                    <td className="text-slate-600">
                                        {r.jenjang === "Sp1" && <div>{r.tahap || "-"}</div>}
                                        <div className="text-[11px] text-slate-400 mt-0.5 whitespace-nowrap">
                                            {formatSemesterPeriode(r)}
                                        </div>
                                    </td>
                                    <td
                                        className="hidden lg:table-cell text-[11px] text-slate-500 max-w-[12rem]"
                                        title={teksLama(r) || undefined}
                                        data-testid={`ppds-teks-lama-${r.id}`}
                                    >
                                        {ringkasTeksLama(teksLama(r)) || <span className="text-slate-300">-</span>}
                                    </td>
                                    <td><span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-semibold ${STATUS_STYLE[r.status_keaktifan] || ""}`}>{r.status_keaktifan}</span></td>
                                    <td>
                                        {r.status_keaktifan === "Cuti" ? (
                                            <span
                                                className="inline-block px-2 py-0.5 rounded-full border text-[11px] font-semibold bg-slate-100 text-slate-500 border-slate-300"
                                                title={`Status Cuti — Stase${r.stase ? ` (${r.stase})` : ""} tidak dihitung dalam agregasi Dashboard selama peserta cuti.`}
                                            >
                                                Tidak dihitung (Cuti)
                                            </span>
                                        ) : r.stase ? (
                                            <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-semibold ${STASE_STYLE[r.stase] || ""}`}>{r.stase}</span>
                                        ) : (
                                            <span className="text-slate-400">-</span>
                                        )}
                                    </td>
                                    <td className="text-[11px] text-slate-500">
                                        <div>{r.updated_by || "-"}</div>
                                        <div className="text-slate-400">{formatWIB(r.updated_at)}</div>
                                    </td>
                                    <td className="text-right pr-2">
                                        <Button size="sm" variant="ghost" disabled={locked} onClick={() => openEdit(r)} data-testid={`peserta-edit-${r.id}`}>
                                            <Pencil className="w-4 h-4" />
                                        </Button>
                                        {canDelete && (
                                            <Button size="sm" variant="ghost" disabled={locked} className="text-rose-600 hover:text-rose-700" onClick={() => setConfirmDelete(r)} data-testid={`peserta-delete-${r.id}`}>
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 py-2 border-t bg-slate-50 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-2">
                    <span>
                        Menampilkan <span className="font-bold text-slate-800">{items.length}</span> dari{" "}
                        <span className="font-bold text-slate-800">{total}</span> peserta didik
                        {canBulk && selectedCount > 0 && (
                            <span className="font-semibold text-sky-700 ml-2" data-testid="peserta-selected-count">
                                · {selectedCount} dipilih
                            </span>
                        )}
                    </span>
                    <div className="flex items-center gap-2" data-testid="peserta-pagination">
                        <Button
                            size="sm" variant="outline" className="h-7 px-2"
                            disabled={page <= 1 || loading}
                            onClick={() => goToPage(page - 1)}
                            data-testid="peserta-page-prev"
                        >
                            <ChevronLeft className="w-3.5 h-3.5" />
                        </Button>
                        <span className="text-slate-600">Halaman {page} dari {totalPages}</span>
                        <Button
                            size="sm" variant="outline" className="h-7 px-2"
                            disabled={page >= totalPages || loading}
                            onClick={() => goToPage(page + 1)}
                            data-testid="peserta-page-next"
                        >
                            <ChevronRight className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Form dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                {/* h-[92vh] (bukan max-h + overflow di luar): dulu seluruh DialogContent
                    (header+tabs+footer) di-scroll sebagai satu blok dan tingginya cuma
                    ngikutin konten, jadi pas pindah tab "Data Peserta" (panjang) <->
                    "Rotasi Klinik" (pendek) tinggi kotak berubah drastis -- dan karena
                    dialog di-posisikan center via translate(-50%,-50%), itu bikin TABS
                    BAR ikut "meloncat" naik/turun tiap ganti tab. Sekarang tinggi kotak
                    TETAP (h-[92vh]), cuma area TabsContent (flex-1 min-h-0) yg discroll
                    sendiri -- header/tab-bar/footer tidak pernah bergeser. */}
                <DialogContent className="max-w-3xl h-[92vh] flex flex-col overflow-hidden">
                    <DialogHeader className="shrink-0">
                        <DialogTitle>{editing ? "Edit Peserta Didik" : "Tambah Peserta Didik"}</DialogTitle>
                    </DialogHeader>
                    <Tabs defaultValue="data" className="w-full flex-1 min-h-0 flex flex-col">
                        {editing && (
                            <TabsList className="mb-3 shrink-0">
                                <TabsTrigger value="data" data-testid="peserta-tab-data">Data Peserta</TabsTrigger>
                                <TabsTrigger value="rotasi" data-testid="peserta-tab-rotasi">Rotasi Klinik</TabsTrigger>
                            </TabsList>
                        )}
                        <TabsContent
                            value="data"
                            className="flex-1 min-h-0 overflow-y-auto pr-1 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-200"
                        >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Nama Lengkap (dgn gelar)" required>
                            <Input data-testid="ppds-form-nama-input" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="dr. Andi Wijaya" />
                        </Field>
                        <Field label="NPM" required>
                            <Input data-testid="ppds-form-npm-input" value={form.npm} onChange={(e) => setForm({ ...form, npm: e.target.value })} placeholder="e.g. 2306012345" />
                        </Field>
                        <Field label="Jenjang" required>
                            <Select value={form.jenjang} onValueChange={(v) => setForm({
                                ...form, jenjang: v, prodi_id: "", tahap: v === "Sp1" ? "1" : "",
                                // "Belum Stase" khusus PPDS: dilepas saat jenjang diganti ke Koas
                                stase: v === "Koas" && form.stase === STASE_BELUM ? "" : form.stase,
                            })} disabled={(!isSuper && !!user.prodi_id) || isKoas}>
                                <SelectTrigger data-testid="ppds-form-jenjang-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Sp1">Sp1</SelectItem>
                                    <SelectItem value="Sp2">Sp2</SelectItem>
                                    <SelectItem value="Koas">Koas</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label={istilah.panjang} required>
                            <ProdiSelect
                                prodi={availableProdi}
                                value={form.prodi_id}
                                onValueChange={(v) => setForm({ ...form, prodi_id: v })}
                                disabled={!isSuper && !!user.prodi_id}
                                showJenjang={false}
                                placeholder={`Pilih ${istilah.kecil}`}
                                searchPlaceholder={`Cari nama ${istilah.kecil}...`}
                                emptyText={`${istilah.singkat} tidak ditemukan.`}
                                testId="ppds-form-prodi-select"
                            />
                        </Field>
                        {form.jenjang === "Sp1" && (
                            <Field label="Tahap" required>
                                <Select
                                    value={form.tahap || ""}
                                    onValueChange={(v) => setForm({
                                        ...form,
                                        tahap: v,
                                        // Tahap MDU/MDK -> Stase dipaksa MDU-MDK; kembali ke 1/2/3 -> lepas kuncian
                                        stase: v === "MDU/MDK"
                                            ? "MDU-MDK"
                                            : (form.stase === "MDU-MDK" ? "" : form.stase),
                                    })}
                                >
                                    <SelectTrigger><SelectValue placeholder="Pilih tahap" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="1">Tahap 1</SelectItem>
                                        <SelectItem value="2">Tahap 2</SelectItem>
                                        <SelectItem value="3">Tahap 3</SelectItem>
                                        <SelectItem value="MDU/MDK">MDU/MDK</SelectItem>
                                    </SelectContent>
                                </Select>
                            </Field>
                        )}
                        {form.jenjang === "Sp2" && (
                            <Field label="Peminatan">
                                <Input value={form.peminatan || ""} onChange={(e) => setForm({ ...form, peminatan: e.target.value })} />
                            </Field>
                        )}
                        <Field label="Kewarganegaraan" required>
                            {/* WNI -> Jenis Identitas otomatis terkunci ke NIK & Negara Penerbit
                                dikosongkan (tidak relevan). Nomor ikut dikosongkan HANYA kalau
                                sebelumnya Paspor (bukan format NIK), supaya NIK yg sudah benar
                                tidak hilang percuma cuma krn kewarganegaraan diganti. */}
                            <Select
                                value={form.kewarganegaraan}
                                onValueChange={(v) => setForm({
                                    ...form,
                                    kewarganegaraan: v,
                                    jenis_identitas: v === "WNI" ? "NIK" : form.jenis_identitas,
                                    negara_penerbit_paspor: v === "WNI" ? "" : form.negara_penerbit_paspor,
                                    nik: v === "WNI" && form.jenis_identitas === "Paspor" ? "" : form.nik,
                                })}
                            >
                                <SelectTrigger data-testid="ppds-form-kewarganegaraan-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="WNI">WNI</SelectItem>
                                    <SelectItem value="WNA">WNA</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Jenis Identitas" required>
                            {/* Terkunci ke NIK selama WNI -- pilihan Paspor cuma muncul stlh WNA
                                dipilih. Ganti jenis -> kosongkan nomor lama supaya tidak ada nomor
                                "nyasar" dari format sebelumnya (mis. sisa NIK 16 digit dikira paspor). */}
                            <Select
                                value={form.jenis_identitas}
                                onValueChange={(v) => setForm({ ...form, jenis_identitas: v, nik: "" })}
                                disabled={form.kewarganegaraan === "WNI"}
                            >
                                <SelectTrigger data-testid="ppds-form-jenis-identitas-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="NIK">NIK</SelectItem>
                                    {form.kewarganegaraan === "WNA" && <SelectItem value="Paspor">Paspor</SelectItem>}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label={form.jenis_identitas === "Paspor" ? "Nomor Paspor" : "NIK (16 digit)"} required>
                            <Input
                                data-testid="ppds-form-nik-input"
                                value={form.nik}
                                maxLength={form.jenis_identitas === "Paspor" ? 20 : 16}
                                onChange={(e) => setForm({
                                    ...form,
                                    nik: form.jenis_identitas === "Paspor"
                                        ? e.target.value.replace(/[^A-Za-z0-9]/g, "")
                                        : e.target.value.replace(/\D/g, ""),
                                })}
                            />
                        </Field>
                        {form.kewarganegaraan === "WNA" && form.jenis_identitas === "Paspor" && (
                            <Field label="Negara Penerbit Paspor" required>
                                <Input
                                    data-testid="ppds-form-negara-paspor-input"
                                    value={form.negara_penerbit_paspor || ""}
                                    onChange={(e) => setForm({ ...form, negara_penerbit_paspor: e.target.value })}
                                />
                            </Field>
                        )}
                        <Field label="Email" required>
                            <Input data-testid="ppds-form-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </Field>
                        <Field label="Tempat Lahir" required={!STATUS_TANPA_SEMESTER_WAJIB.includes(form.status_keaktifan)}>
                            <Input
                                className={bioInvalid.includes("tempat_lahir") ? "border-rose-500" : ""}
                                value={form.tempat_lahir || ""}
                                onChange={(e) => setForm({ ...form, tempat_lahir: e.target.value })}
                            />
                        </Field>
                        <Field label="Asal Universitas">
                            <Input value={form.asal_universitas || ""} onChange={(e) => setForm({ ...form, asal_universitas: e.target.value })} placeholder="Universitas Indonesia" />
                        </Field>
                        <Field label="Tanggal Lahir" required={!STATUS_TANPA_SEMESTER_WAJIB.includes(form.status_keaktifan)}>
                            <Input
                                type="date"
                                className={bioInvalid.includes("tanggal_lahir") ? "border-rose-500" : ""}
                                value={form.tanggal_lahir || ""}
                                onChange={(e) => setForm({ ...form, tanggal_lahir: e.target.value })}
                            />
                        </Field>
                        <Field label="No. HP" required={!STATUS_TANPA_SEMESTER_WAJIB.includes(form.status_keaktifan)}>
                            <Input
                                data-testid="ppds-form-no-hp-input"
                                className={bioInvalid.includes("no_hp") || galatNoHp ? "border-rose-500" : ""}
                                value={form.no_hp || ""}
                                inputMode={wniForm ? "numeric" : undefined}
                                maxLength={wniForm ? PANJANG_MAKS_NO_HP : undefined}
                                placeholder={wniForm ? CONTOH_NO_HP : undefined}
                                aria-invalid={!!galatNoHp}
                                onChange={(e) => ubahNoHp(e.target.value)}
                                onPaste={(e) => {
                                    if (!wniForm) return;
                                    e.preventDefault();
                                    ubahNoHp(bersihkanTempelanNoHp(e.clipboardData.getData("text")));
                                }}
                            />
                            {galatNoHp
                                ? <p className="text-xs text-rose-600 mt-1" data-testid="ppds-form-no-hp-galat">{galatNoHp}</p>
                                : wniForm && <p className="text-xs text-slate-500 mt-1">{BANTUAN_NO_HP}</p>}
                        </Field>
                        <Field label="Semester" required={!STATUS_TANPA_SEMESTER_WAJIB.includes(form.status_keaktifan)}>
                            <Select
                                value={form.semester != null ? String(form.semester) : "__none__"}
                                onValueChange={(v) => setForm({ ...form, semester: v === "__none__" ? null : v })}
                            >
                                <SelectTrigger data-testid="ppds-form-semester-select"><SelectValue placeholder="Pilih semester" /></SelectTrigger>
                                <SelectContent>
                                    {STATUS_TANPA_SEMESTER_WAJIB.includes(form.status_keaktifan) && (
                                        <SelectItem value="__none__">Tidak diisi</SelectItem>
                                    )}
                                    {SEMESTER_OPTIONS.map((n) => (
                                        <SelectItem key={n} value={String(n)}>Semester {n}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Periode Masuk" required={!STATUS_TANPA_SEMESTER_WAJIB.includes(form.status_keaktifan)}>
                            <Select
                                value={form.periode && form.tahun_akademik_mulai != null ? `${form.periode}-${form.tahun_akademik_mulai}` : "__none__"}
                                onValueChange={(v) => {
                                    if (v === "__none__") {
                                        setForm({ ...form, periode: null, tahun_akademik_mulai: null });
                                        return;
                                    }
                                    const opt = PERIODE_MASUK_OPTIONS.find((o) => o.value === v);
                                    setForm({ ...form, periode: opt.periode, tahun_akademik_mulai: opt.tahun });
                                }}
                            >
                                <SelectTrigger data-testid="ppds-form-periode-masuk-select"><SelectValue placeholder="Pilih periode masuk" /></SelectTrigger>
                                <SelectContent>
                                    {STATUS_TANPA_SEMESTER_WAJIB.includes(form.status_keaktifan) && (
                                        <SelectItem value="__none__">Tidak diisi</SelectItem>
                                    )}
                                    {PERIODE_MASUK_OPTIONS.map((o) => (
                                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        {!!teksLama(form) && (
                            <Field label="Teks lama Semester & Tahun Masuk">
                                <div className="pt-1 space-y-0.5" data-testid="ppds-form-semester-arsip">
                                    <div className="text-sm text-slate-700 break-words">{teksLama(form)}</div>
                                    <div className="text-[11px] text-slate-500">
                                        Baca-saja, dari data awal — acuan untuk mengisi Semester &amp; Periode Masuk.
                                    </div>
                                </div>
                            </Field>
                        )}
                        <Field label="Status Keaktifan" required>
                            <Select value={form.status_keaktifan} onValueChange={(v) => setForm({ ...form, status_keaktifan: v })}>
                                <SelectTrigger data-testid="ppds-form-status-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Aktif">Aktif</SelectItem>
                                    <SelectItem value="Tidak Aktif">Tidak Aktif</SelectItem>
                                    <SelectItem value="Cuti">Cuti</SelectItem>
                                    <SelectItem value="Lulus">Lulus</SelectItem>
                                    <SelectItem value="Meninggal">Meninggal</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                        {form.status_keaktifan === "Lulus" && (
                            <Field label="Bulan/Tahun Lulus" required={!editing || editing.status_keaktifan !== "Lulus"}>
                                <Input value={form.bulan_tahun_lulus || ""} onChange={(e) => setForm({ ...form, bulan_tahun_lulus: e.target.value })} placeholder="Juli 2027" />
                            </Field>
                        )}
                        <Field label="Stase" required={!STATUS_TANPA_STASE.includes(form.status_keaktifan)}>
                            <Select
                                value={form.stase || "__none__"}
                                onValueChange={(v) => setForm({ ...form, stase: v === "__none__" ? "" : v })}
                                disabled={form.tahap === "MDU/MDK"}
                            >
                                <SelectTrigger><SelectValue placeholder="Pilih stase" /></SelectTrigger>
                                <SelectContent>
                                    {STATUS_TANPA_STASE.includes(form.status_keaktifan) && (
                                        <SelectItem value="__none__">Tidak ada</SelectItem>
                                    )}
                                    {opsiStase(form.jenjang).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            {form.tahap === "MDU/MDK" && (
                                <p className="text-[11px] text-slate-500 mt-1">Terkunci "MDU-MDK" karena Tahap = MDU/MDK.</p>
                            )}
                            {editing && (
                                <KunciStaseRotasi pesertaId={editing.id} terkunciAwal={staseTerkunci} role={user.role}
                                    stase={form.stase} status={form.status_keaktifan} />
                            )}
                        </Field>
                        <Field label="Pembiayaan Pendidikan">
                            <Input value={form.pembiayaan_pendidikan || ""} onChange={(e) => setForm({ ...form, pembiayaan_pendidikan: e.target.value })} />
                        </Field>
                        {form.jenjang === "Sp2" && (
                            <Field label="Keterangan Pegawai RSCM">
                                <Input value={form.keterangan_pegawai_rscm || ""} onChange={(e) => setForm({ ...form, keterangan_pegawai_rscm: e.target.value })} />
                            </Field>
                        )}
                        <Field label="Alamat" full required={!STATUS_TANPA_SEMESTER_WAJIB.includes(form.status_keaktifan)}>
                            <Textarea
                                rows={2}
                                className={bioInvalid.includes("alamat") ? "border-rose-500" : ""}
                                value={form.alamat || ""}
                                onChange={(e) => setForm({ ...form, alamat: e.target.value })}
                            />
                        </Field>
                        <Field label="Catatan Lain" full>
                            <Textarea rows={2} value={form.catatan_lain || ""} onChange={(e) => setForm({ ...form, catatan_lain: e.target.value })} />
                        </Field>

                        {editing && bolehUbahPeriodeCuti(user.role) && periodeCuti && (
                            <PeriodeCutiSection pesertaId={editing.id} periodeAwal={periodeCuti} locked={locked}
                                onTersimpan={setPeriodeCuti} />
                        )}

                        {editing && rscmInfo && (
                            <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5">
                                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                        Jumlah Hari Stase RSCM — {rscmInfo.periode_label}
                                    </Label>
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                                        rscmInfo.sumber === "koreksi_manual" ? "bg-blue-100 text-blue-700 border-blue-300"
                                        : rscmInfo.sumber === "asumsi" ? "bg-amber-100 text-amber-700 border-amber-300"
                                        : "bg-slate-100 text-slate-600 border-slate-300"}`}>
                                        {rscmInfo.sumber === "koreksi_manual" ? "Dikoreksi Manual"
                                            : rscmInfo.sumber === "asumsi" ? "Asumsi (perlu verifikasi)" : "Otomatis"}
                                    </span>
                                </div>
                                <div className="mt-2 flex items-center gap-2">
                                    <Input
                                        type="number" min={0} max={31}
                                        className="h-9 w-24"
                                        value={rscmDraft}
                                        disabled={!rscmInfo.dapat_koreksi || rscmBusy}
                                        onChange={(e) => setRscmDraft(e.target.value)}
                                        data-testid="rscm-input"
                                    />
                                    <span className="text-xs text-slate-500">hari (otomatis: {rscmInfo.hari_rscm_otomatis})</span>
                                    {rscmInfo.dapat_koreksi ? (
                                        <>
                                            <Button size="sm" disabled={rscmBusy || rscmDraft === ""} onClick={() => saveRscm(false)} className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="rscm-save">
                                                Simpan Koreksi
                                            </Button>
                                            {rscmInfo.sumber === "koreksi_manual" && (
                                                <Button size="sm" variant="outline" disabled={rscmBusy} onClick={() => saveRscm(true)}>
                                                    Kembalikan Otomatis
                                                </Button>
                                            )}
                                        </>
                                    ) : (
                                        <span className="text-[11px] text-amber-600">Jendela koreksi ditutup — hanya bisa dilihat.</span>
                                    )}
                                </div>
                                {rscmInfo.sumber === "koreksi_manual" && rscmInfo.dikoreksi_oleh && (
                                    <div className="text-[11px] text-slate-400 mt-1">
                                        Dikoreksi oleh {rscmInfo.dikoreksi_oleh}
                                        {rscmInfo.dikoreksi_pada ? ` · ${formatWIB(rscmInfo.dikoreksi_pada)}` : ""}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                        </TabsContent>
                        {editing && (
                            <TabsContent
                                value="rotasi"
                                className="flex-1 min-h-0 overflow-y-auto pr-1 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-200"
                            >
                                <RotasiKlinikTab pesertaId={editing.id} pesertaNama={editing.nama} locked={locked} />
                            </TabsContent>
                        )}
                    </Tabs>
                    <DialogFooter className="shrink-0">
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
                        <Button onClick={submit} data-testid="ppds-form-submit" className="bg-[#00B9AD] hover:bg-[#60C0D0]">
                            {editing ? "Simpan Perubahan" : "Tambah"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Hapus peserta didik?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmDelete?.nama} (NPM: {confirmDelete?.npm}) akan dihapus permanen.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction data-testid="peserta-delete-confirm" className="bg-rose-600 hover:bg-rose-700" onClick={doDelete}>Hapus</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Kirim ke Orientasi RS -- kategori otomatis dari jenjang, tidak perlu dipilih manual */}
            <AlertDialog open={kirimOrientasiOpen} onOpenChange={(o) => !kirimOrientasiBusy && setKirimOrientasiOpen(o)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Kirim {selectedCount} peserta ke Orientasi RS?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {kirimOrientasiBreakdown.sp1 > 0 && <>{kirimOrientasiBreakdown.sp1} sbg Spesialis (Sp1). </>}
                            {kirimOrientasiBreakdown.sp2 > 0 && <>{kirimOrientasiBreakdown.sp2} sbg Subspesialis (Sp2). </>}
                            {kirimOrientasiBreakdown.koas > 0 && <>{kirimOrientasiBreakdown.koas} sbg Koas. </>}
                            Hanya peserta yang statusnya masih "MDU/MDK" (Tahap MDU/MDK untuk Sp1, Stase MDU-MDK untuk Sp2
                            dan Koas) dan Aktif yang akan benar-benar dikirim; sisanya dilewati dan dilaporkan, bukan
                            menggagalkan yang lain. Peserta yang sudah punya entri Orientasi yang masih berjalan juga akan
                            dilewati (tidak dobel).
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={kirimOrientasiBusy}>Batal</AlertDialogCancel>
                        <AlertDialogAction data-testid="kirim-orientasi-confirm" disabled={kirimOrientasiBusy} className="bg-emerald-600 hover:bg-emerald-700" onClick={doKirimOrientasi}>
                            {kirimOrientasiBusy ? "Memproses..." : `Ya, Kirim ${selectedCount} Peserta`}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Ubah Semester massal -- 2 field independen (Semester & Periode Masuk),
                masing2 boleh "tidak diubah". Peserta Lulus/Meninggal: Semester dilewati. */}
            <Dialog open={semesterBulkOpen} onOpenChange={(o) => !semesterBulkBusy && (o ? setSemesterBulkOpen(true) : closeSemesterBulk())}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Ubah Semester — {selectedCount} peserta</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <Field label="Semester">
                            <Select value={semesterBulkSemester} onValueChange={setSemesterBulkSemester}>
                                <SelectTrigger data-testid="bulk-semester-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__skip__">— Tidak diubah —</SelectItem>
                                    {SEMESTER_OPTIONS.map((n) => (
                                        <SelectItem key={n} value={String(n)}>Semester {n}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Periode Masuk">
                            <Select value={semesterBulkPeriode} onValueChange={setSemesterBulkPeriode}>
                                <SelectTrigger data-testid="bulk-periode-masuk-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__skip__">— Tidak diubah —</SelectItem>
                                    {PERIODE_MASUK_OPTIONS.map((o) => (
                                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <p className="text-xs text-slate-500">
                            Peserta berstatus Lulus/Meninggal: bagian Semester akan dilewati (tidak wajib), Periode Masuk tetap diterapkan.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={closeSemesterBulk} disabled={semesterBulkBusy}>Batal</Button>
                        <Button
                            data-testid="bulk-semester-confirm"
                            disabled={semesterBulkBusy || (semesterBulkSemester === "__skip__" && semesterBulkPeriode === "__skip__")}
                            onClick={applySemesterBulk}
                        >
                            {semesterBulkBusy ? "Memproses..." : "Terapkan"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk edit dialog (2 langkah: pilih nilai -> konfirmasi) */}
            <Dialog
                open={!!bulkField}
                onOpenChange={(o) => { if (!o) { setBulkField(null); setBulkConfirm(false); setBulkValue(""); } }}
            >
                <DialogContent className={isProdiBulk ? "max-w-lg" : "max-w-md"}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <PencilRuler className="w-4 h-4 text-teal-600" />
                            {isProdiBulk ? "Ubah Prodi" : `Ubah ${bulkField ? BULK_FIELDS[bulkField].label : ""}`} — {selectedCount} peserta
                        </DialogTitle>
                    </DialogHeader>

                    {!bulkConfirm ? (
                        <>
                            {isProdiBulk ? (
                                <div className="space-y-3">
                                    <div className="space-y-2">
                                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                            Prodi Tujuan
                                        </Label>
                                        <ProdiSelect
                                            prodi={prodiList}
                                            value={bulkValue}
                                            onValueChange={setBulkValue}
                                            placeholder="Pilih prodi tujuan"
                                            testId="bulk-value-select"
                                        />
                                    </div>

                                    {npmNotFound.length > 0 && (
                                        <div className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-800">
                                            <div className="font-semibold mb-1">{npmNotFound.length} NPM dari daftar tidak ditemukan di tabel (tidak akan ikut dipindah):</div>
                                            <div className="font-mono break-words">{npmNotFound.join(", ")}</div>
                                        </div>
                                    )}

                                    <div>
                                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                            Preview perpindahan ({selectedCount} peserta)
                                        </Label>
                                        {selectAllFilterActive ? (
                                            // Mode "Pilih Semua sesuai filter" -- tidak ada daftar ID per-baris di
                                            // frontend (audit performa 28 Sep 2026, temuan #10), jadi preview-nya
                                            // ringkasan kriteria, bukan daftar nama satu-per-satu.
                                            <div className="mt-1 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                                                Semua <span className="font-bold">{selectedCount} peserta</span> yang sesuai filter/pencarian
                                                yang sedang aktif di tabel akan dipindahkan ke{" "}
                                                <span className={bulkValue ? "font-semibold text-teal-700" : ""}>
                                                    {bulkValue ? (prodiMap[bulkValue]?.nama_prodi || "-") : "(pilih prodi)"}
                                                </span>{" "}
                                                -- bukan cuma yang tampil di halaman ini.
                                            </div>
                                        ) : (
                                            <div className="mt-1 max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                                                {selectedRows.map((r) => (
                                                    <div key={r.id} className="px-3 py-1.5 text-xs flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5 sm:gap-2">
                                                        <span className="font-semibold text-slate-800 min-w-0 truncate">
                                                            {r.nama} <span className="font-mono text-slate-400">({r.npm})</span>
                                                        </span>
                                                        <span className="text-slate-500 sm:text-right">
                                                            {prodiMap[r.prodi_id]?.nama_prodi || "-"} <span className="text-slate-400">→</span>{" "}
                                                            <span className={bulkValue ? "font-semibold text-teal-700" : "text-slate-400"}>
                                                                {bulkValue ? (prodiMap[bulkValue]?.nama_prodi || "-") : "(pilih prodi)"}
                                                            </span>
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                        Nilai baru
                                    </Label>
                                    <Select value={bulkValue} onValueChange={setBulkValue}>
                                        <SelectTrigger data-testid="bulk-value-select">
                                            <SelectValue placeholder={`Pilih ${bulkField ? BULK_FIELDS[bulkField].label : ""}`} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {bulkField && (bulkField === "stase" ? opsiStaseFilter : BULK_FIELDS[bulkField].options).map((o) => (
                                                <SelectItem key={o} value={o}>{o}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-[11px] text-slate-500">
                                        Nilai ini akan diterapkan ke semua {selectedCount} peserta yang dipilih.
                                        Baris yang tidak lolos validasi akan dilaporkan tanpa menggagalkan yang lain.
                                    </p>
                                </div>
                            )}
                            <DialogFooter>
                                <Button variant="outline" onClick={() => { setBulkField(null); setBulkValue(""); }}>Batal</Button>
                                <Button
                                    className="bg-[#00B9AD] hover:bg-[#60C0D0]"
                                    disabled={!bulkValue}
                                    onClick={() => setBulkConfirm(true)}
                                    data-testid="bulk-apply"
                                >
                                    {isProdiBulk ? "Lanjutkan" : `Terapkan ke ${selectedCount} Peserta`}
                                </Button>
                            </DialogFooter>
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-slate-700">
                                {isProdiBulk ? (
                                    <>
                                        Yakin pindahkan <span className="font-bold">{selectedCount} peserta</span> ke prodi{" "}
                                        <span className="font-bold">"{prodiMap[bulkValue]?.nama_prodi || "-"}"</span>?
                                        {npmNotFound.length > 0 && (
                                            <span className="block mt-2 text-amber-700 text-xs font-semibold">
                                                Catatan: {npmNotFound.length} NPM dari daftar yang Anda paste tidak ditemukan dan TIDAK ikut dipindah.
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        Yakin ubah <span className="font-bold">{bulkField ? BULK_FIELDS[bulkField].label : ""}</span>{" "}
                                        <span className="font-bold">{selectedCount} peserta</span> jadi{" "}
                                        <span className="font-bold">"{bulkValue}"</span>?
                                    </>
                                )}
                            </p>
                            <DialogFooter>
                                <Button variant="outline" disabled={bulkBusy} onClick={() => setBulkConfirm(false)}>Kembali</Button>
                                <Button
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                    disabled={bulkBusy}
                                    onClick={applyBulk}
                                    data-testid="bulk-confirm"
                                >
                                    {bulkBusy ? "Memproses..." : (isProdiBulk ? "Ya, Pindahkan" : "Ya, Terapkan")}
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Pilih peserta via daftar NPM */}
            <Dialog open={npmPickerOpen} onOpenChange={setNpmPickerOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ListChecks className="w-4 h-4 text-teal-600" />
                            Pilih Peserta via Daftar NPM
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                            Daftar NPM (satu per baris, atau dipisah koma)
                        </Label>
                        <Textarea
                            rows={8}
                            value={npmPickerText}
                            onChange={(e) => setNpmPickerText(e.target.value)}
                            placeholder={"2306012345\n2306012346\n..."}
                            data-testid="npm-picker-textarea"
                        />
                        <p className="text-[11px] text-slate-500">
                            Sistem akan mencocokkan NPM terhadap semua peserta sesuai filter yang sedang aktif
                            (lintas halaman). NPM yang tidak ditemukan akan dilaporkan, bukan diam-diam dilewati.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setNpmPickerOpen(false)}>Batal</Button>
                        <Button
                            className="bg-[#00B9AD] hover:bg-[#60C0D0]"
                            onClick={applyNpmPicker}
                            disabled={npmLookupLoading}
                            data-testid="npm-picker-apply"
                        >
                            {npmLookupLoading ? "Memuat daftar..." : "Pilih Peserta"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Hasil bulk edit */}
            <Dialog open={!!bulkResult} onOpenChange={(o) => { if (!o) setBulkResult(null); }}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Hasil Ubah Massal</DialogTitle>
                    </DialogHeader>
                    {bulkResult && (
                        <div className="space-y-3">
                            <div className="flex gap-3">
                                <div className="flex-1 rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                                    <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">Berhasil</div>
                                    <div className="text-2xl font-extrabold text-emerald-700">{bulkResult.updated}</div>
                                </div>
                                <div className="flex-1 rounded-lg bg-rose-50 border border-rose-200 p-3">
                                    <div className="text-[11px] uppercase tracking-widest text-rose-700 font-semibold">Gagal</div>
                                    <div className="text-2xl font-extrabold text-rose-700">{bulkResult.failed.length}</div>
                                </div>
                            </div>
                            <div className="text-xs text-slate-500">
                                {bulkResult.field_label} → <span className="font-semibold">"{bulkResult.field === "prodi_id" ? (bulkResult.value_label || prodiMap[bulkResult.value]?.nama_prodi || bulkResult.value) : bulkResult.value}"</span>, dari total {bulkResult.total} dipilih.
                            </div>
                            {bulkResult.failed.length > 0 && (
                                <div className="border border-slate-200 rounded-lg overflow-hidden">
                                    <table className="w-full compact">
                                        <thead className="bg-slate-100">
                                            <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                                <th>Nama</th><th>NPM</th><th>Alasan</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {bulkResult.failed.map((f, i) => (
                                                <tr key={i} className="border-t border-slate-200">
                                                    <td className="font-semibold text-slate-800">{f.nama || "-"}</td>
                                                    <td className="font-mono text-xs">{f.npm || "-"}</td>
                                                    <td className="text-xs text-rose-700">{f.error}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button className="bg-[#00B9AD] hover:bg-[#60C0D0]" onClick={() => setBulkResult(null)}>Selesai</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ImportPesertaDialog
                open={importOpen}
                onOpenChange={setImportOpen}
                prodiList={prodiList}
                onImported={load}
            />
        </div>
    );
}

function Field({ label, children, required, full }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                {label} {required && <span className="text-rose-500">*</span>}
            </Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
